import React, { useEffect, useState } from 'react'
import { Button, Select, InputNumber, DatePicker, Alert, Progress, message } from 'antd'
import { PlusOutlined, DeleteOutlined, InfoCircleOutlined, SendOutlined } from '@ant-design/icons'
import dayjs, { Dayjs } from 'dayjs'
import { useMasterData } from '@/context/MasterDataContext'
import { mappingStore, GeneratorMapping, ConsumerRow } from './mappingStore'
import { submitForApproval } from './mappingApproval'
import { resolveLeadProposalTariff } from '@/utils/leadTariff'
import { annualToMonthlyUnits, formatUnitsKWh } from '@/utils/numberFormat'

interface ConsumerEntry {
  key: string
  consumerId: string
  shareHolding: number | null      // Allocation %
  allocatedUnits: number | null    // Allocated units
  effectiveFrom: Dayjs | null
  effectiveTo: Dayjs | null
  tariff: number | null            // Sakthi Power Tariff
  tnebTariff: number | null
  tradeMargin: number | null
  status: 'active' | 'suspended'
}

const newRow = (): ConsumerEntry => ({
  key: `${Date.now()}-${Math.round(performance.now())}`,
  consumerId: '', shareHolding: null, allocatedUnits: null,
  effectiveFrom: null, effectiveTo: null,
  tariff: null, tnebTariff: null, tradeMargin: null, status: 'active',
})

interface Props {
  /** Generator this allocation belongs to (fixed — chosen by the caller). */
  generatorId: string
  /** Existing mapping/group id to edit; omit to create a new plant group. */
  mappingId?: string
  /** Pre-selected plant ID when creating a new mapping group. */
  initialPlantId?: string
  /** Fired after a successful save. */
  onSaved?: () => void
  /** Fired when the user cancels (e.g. collapse the inline panel). */
  onCancel?: () => void
  /** Tighter spacing for the in-row (list) variant. */
  compact?: boolean
}

/**
 * Plant selection + consumer allocation editor for a single generator.
 * Shared by the full-page MappingForm and the inline Mapping list expand row.
 */
const ConsumerAllocationEditor: React.FC<Props> = ({ generatorId, mappingId, initialPlantId, onSaved, onCancel, compact }) => {
  const { generators: masterGenerators, consumers: masterConsumers, tariffs, tnebTariffs } = useMasterData()

  const [plantId, setPlantId] = useState<string>('')
  const [consumers, setConsumers] = useState<ConsumerEntry[]>([newRow()])
  const [saving, setSaving] = useState(false)

  const selectedGen   = masterGenerators.find(g => g.id === generatorId)
  const plantList     = selectedGen?.plants ?? []
  const plantOptions  = plantList
    .filter(p => p.status !== 'inactive')
    .map(p => ({ label: `${p.plantName || p.id} — ${p.htServiceNumber || 'no HT'} · ${Number(p.capacity || 0).toLocaleString('en-IN')} kWh/yr (~${formatUnitsKWh(annualToMonthlyUnits(p.capacity))}/mo)`, value: p.id }))
  const selectedPlant = plantList.find(p => p.id === plantId)
  // Stored/displayed "per annum" figure — kept as-is for records (e.g. the mapping's
  // saved plantCapacity/capacity fields, used elsewhere for annual reporting).
  const plantCapacity = Number(selectedPlant?.capacity || 0)
  // Allocation itself runs per billing cycle (monthly), so the ceiling/default math
  // below is based on the plant's derived monthly generation, not the raw annual figure.
  // Rounded to whole kWh so it feeds cleanly into the unit inputs below.
  const plantMonthlyCapacity = Math.round(annualToMonthlyUnits(plantCapacity))

  // A consumer must never be allocated more than it actually consumes (per annum, as stored).
  const consumerAnnualCapacity = (consumerId: string): number => {
    const consumer = (masterConsumers || []).find(c => c && c.id === consumerId)
    if (!consumer) return 0
    return (consumer.services || [])
      .filter(s => s && s.status === 'active')
      .reduce((sum, s) => sum + Number(s.avgAnnualConsumption || 0), 0)
  }
  // Monthly equivalent — this is what allocation is actually capped against.
  // Rounded to whole kWh so it feeds cleanly into the unit inputs below.
  const consumerMonthlyCapacity = (consumerId: string): number => Math.round(annualToMonthlyUnits(consumerAnnualCapacity(consumerId)))

  const consumerOptions = (masterConsumers || [])
    .filter(c => c && c.status === 'active')
    .map(c => {
      const annualCap = consumerAnnualCapacity(c.id)
      return {
        label: `${c.name} (${c.code || c.id})${annualCap > 0 ? ` · ${annualCap.toLocaleString('en-IN')} kWh/yr (~${formatUnitsKWh(annualToMonthlyUnits(annualCap))}/mo)` : ''}`,
        value: c.id,
      }
    })

  // Prefill when editing an existing group; reset when the target changes.
  useEffect(() => {
    if (mappingId) {
      const existing = mappingStore.get(mappingId)
      if (existing) {
        setPlantId(existing.plantId ?? '')
        setConsumers(existing.consumers.length
          ? existing.consumers.map((c, i) => ({
              key: `${Date.now()}-${i}`,
              consumerId: c.consumerId,
              shareHolding: c.shareHolding,
              allocatedUnits: c.allocatedUnits ?? null,
              effectiveFrom: c.effectiveFrom ? dayjs(c.effectiveFrom) : null,
              effectiveTo: c.effectiveTo ? dayjs(c.effectiveTo) : null,
              tariff: c.tariff,
              tnebTariff: c.tnebTariff ?? null,
              tradeMargin: c.tradeMargin,
              status: c.status,
            }))
          : [newRow()])
        return
      }
    }
    // New group — start clean. Preselect the plant passed in (e.g. from the
    // "Map Consumers to this Plant" unmapped-plant action), else auto-pick
    // the only plant if there's just one.
    setConsumers([newRow()])
    setPlantId(initialPlantId || (plantList.length === 1 ? plantList[0].id : ''))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mappingId, generatorId, initialPlantId])

  const totalShare = consumers.reduce((s, c) => s + Number(c.shareHolding ?? 0), 0)
  const totalUnits = consumers.reduce((s, c) => s + Number(c.allocatedUnits ?? 0), 0)
  const isBalanced = totalShare === 100
  const isOver     = totalShare > 100
  const unitsOver  = plantMonthlyCapacity > 0 && totalUnits > plantMonthlyCapacity
  const overConsumerRows = consumers.filter(c => {
    const cap = consumerMonthlyCapacity(c.consumerId)
    return cap > 0 && Number(c.allocatedUnits ?? 0) > cap
  })
  const anyConsumerOver = overConsumerRows.length > 0

  const updateConsumer = (key: string, field: keyof ConsumerEntry, value: any) =>
    setConsumers(prev => prev.map(c => c.key === key ? { ...c, [field]: value } : c))

  const addConsumer = () => {
    if (consumers.length >= 10) { message.warning('Maximum 10 consumers per plant group'); return }
    setConsumers(prev => [...prev, newRow()])
  }
  const removeConsumer = (key: string) => {
    if (consumers.length === 1) { message.warning('At least one consumer is required'); return }
    setConsumers(prev => prev.filter(c => c.key !== key))
  }

  const onConsumerSelect = (key: string, val: string) => {
    const consumer = masterConsumers.find(c => c.id === val)
    // Prefer the Sakthi PF Charge this consumer's own lead already agreed to
    // on its (Won/Approved) Proposal — that's the rate actually negotiated
    // with them, not just whatever Tariff Master happens to resolve to.
    // Tariff Master is only consulted as a fallback, for consumers with no
    // closed proposal of their own (legacy records, generator-only leads, …).
    const leadTariff = consumer ? resolveLeadProposalTariff('Consumer', consumer.name) : null
    const specificTariff = leadTariff == null
      ? (tariffs.find(t => t.status === 'active' && t.generatorId === generatorId && t.consumerId === val)
        || tariffs.find(t => t.status === 'active' && t.generatorId === generatorId && !t.consumerId)
        || tariffs.find(t => t.status === 'active' && !t.generatorId && t.consumerId === val)
        || tariffs.find(t => t.status === 'active' && t.tariffCode === consumer?.tariffCode))
      : undefined
    const rate = leadTariff ?? (specificTariff ? Number(specificTariff.powerFacilitationCharge ?? specificTariff.ratePerUnit) || 0 : 0)
    const specificTneb = tnebTariffs.find(t => t.status === 'active' && consumer &&
      (t.code === consumer.powerTariffCategory || t.category === consumer.powerTariffCategory))
    const tnebRate = specificTneb ? Number(specificTneb.ratePerUnit) || 0 : 0

    // Consumer's monthly consumption capacity across active HT services (from Consumer Master / Lead)
    const consumerCap = consumerMonthlyCapacity(val)

    // Other allocated units assigned to other consumers in this group (monthly)
    const otherUnits = consumers.filter(c => c.key !== key).reduce((s, c) => s + Number(c.allocatedUnits || 0), 0)
    const remainingGenCap = plantMonthlyCapacity > 0 ? Math.max(0, plantMonthlyCapacity - otherUnits) : 0
    const remainingShare  = Math.max(0, 100 - consumers.filter(c => c.key !== key).reduce((s, c) => s + Number(c.shareHolding || 0), 0))

    // Default allocated units (per month):
    // Takes directly from Consumer's Avg Monthly Consumption, capped by remaining monthly plant generation
    let defaultUnits: number | null = null
    if (consumerCap > 0) {
      defaultUnits = remainingGenCap > 0 ? Math.min(consumerCap, remainingGenCap) : consumerCap
    } else if (plantMonthlyCapacity > 0) {
      defaultUnits = Math.round(plantMonthlyCapacity * (remainingShare / 100))
    }

    // Default shareholding %: (defaultUnits / plantMonthlyCapacity) * 100
    let defaultShare: number | null = null
    if (plantMonthlyCapacity > 0 && defaultUnits != null) {
      defaultShare = Number(((defaultUnits / plantMonthlyCapacity) * 100).toFixed(2))
    } else if (remainingShare > 0) {
      defaultShare = remainingShare
    }

    setConsumers(prev => prev.map(c => c.key === key ? {
      ...c, consumerId: val, tariff: rate, tnebTariff: tnebRate,
      tradeMargin: c.tradeMargin ?? 0.25,
      shareHolding: c.shareHolding ?? defaultShare,
      allocatedUnits: c.allocatedUnits ?? defaultUnits,
      effectiveFrom: c.effectiveFrom ?? dayjs(),
    } : c))
  }

  // Suggest allocated units (monthly) = share % of plant's monthly generation (editable)
  const onShareChange = (key: string, share: number | null) => {
    setConsumers(prev => prev.map(c => {
      if (c.key !== key) return c
      const consumerCap = consumerMonthlyCapacity(c.consumerId)
      let units = plantMonthlyCapacity > 0 && share != null ? Math.round(plantMonthlyCapacity * share / 100) : c.allocatedUnits
      let finalShare = share
      if (consumerCap > 0 && units != null && units > consumerCap) {
        // Clamp to what this consumer can actually take — and bring the share %
        // back down to match, so it doesn't overstate the plant as fully
        // allocated while capacity actually remains free for another consumer.
        units = consumerCap
        finalShare = plantMonthlyCapacity > 0 ? Number(((units / plantMonthlyCapacity) * 100).toFixed(2)) : finalShare
        message.info(`Capped to ${masterConsumers.find(m => m.id === c.consumerId)?.name || 'this consumer'}'s Avg Monthly Consumption — share adjusted to ${finalShare}%.`)
      }
      return { ...c, shareHolding: finalShare, allocatedUnits: units }
    }))
  }

  // Suggest share % when manually typing allocated (monthly) units
  const onAllocatedUnitsChange = (key: string, units: number | null) => {
    setConsumers(prev => prev.map(c => {
      if (c.key !== key) return c
      const share = plantMonthlyCapacity > 0 && units != null ? Number(((units / plantMonthlyCapacity) * 100).toFixed(2)) : c.shareHolding
      return { ...c, allocatedUnits: units, shareHolding: share }
    }))
  }

  const handleSave = (submit = false) => {
    setSaving(true)
    try {
      if (!generatorId) { message.error('No generator selected'); return }
      if (!plantId) { message.error('Select a plant'); return }
      if (isOver) { message.error('Total allocation exceeds 100%. Please fix before saving.'); return }
      if (unitsOver) { message.error(`Total allocated units (${totalUnits.toLocaleString('en-IN')}) exceed the plant's proposed monthly generation (${Math.round(plantMonthlyCapacity).toLocaleString('en-IN')}).`); return }
      if (anyConsumerOver) {
        const names = overConsumerRows.map(c => masterConsumers.find(m => m.id === c.consumerId)?.name ?? c.consumerId).join(', ')
        message.error(`Allocated units exceed the consumer's Avg Monthly Consumption for: ${names}.`)
        return
      }
      if (consumers.some(c => !c.consumerId || !c.shareHolding || !c.allocatedUnits || !c.effectiveFrom)) {
        message.error('Each consumer needs: Consumer, Allocation %, Allocated Units and Effective From.'); return
      }

      // Rule §19: one active group per generator + plant — editable, not duplicated
      const dup = mappingStore.getAll().find(m =>
        m.id !== mappingId && m.status === 'active' && m.generatorId === generatorId && m.plantId === plantId)
      if (dup) {
        message.error('An active group already exists for this generator + plant. Edit that group instead of creating a duplicate.')
        return
      }

      const consumerRows: ConsumerRow[] = consumers.map(c => ({
        consumerId: c.consumerId,
        consumerName: masterConsumers.find(m => m.id === c.consumerId)?.name ?? c.consumerId,
        shareHolding: c.shareHolding ?? 0,
        allocatedUnits: c.allocatedUnits ?? 0,
        effectiveFrom: c.effectiveFrom ? c.effectiveFrom.format('YYYY-MM-DD') : '',
        effectiveTo: c.effectiveTo ? c.effectiveTo.format('YYYY-MM-DD') : '',
        tariff: c.tariff ?? 0,
        tnebTariff: c.tnebTariff ?? 0,
        tradeMargin: c.tradeMargin ?? 0,
        status: c.status,
      }))

      const record: Omit<GeneratorMapping, 'id'> = {
        generatorId,
        generatorName: selectedGen?.name ?? generatorId,
        plantId,
        plantName: selectedPlant?.plantName ?? '',
        htNumber: selectedPlant?.htServiceNumber ?? '',
        plantLocation: selectedPlant?.plantAddress ?? '',
        substation: selectedPlant?.substationName ?? '',
        plantCapacity,
        plantType: (selectedPlant?.typeOfPower ?? []).join(' + ') || selectedGen?.plantType || '',
        capacity: plantCapacity ? `${(plantCapacity / 1000).toFixed(1)} MWh/yr` : (selectedGen?.capacity ? `${(selectedGen.capacity / 1000).toFixed(1)} MWh/yr` : ''),
        consumers: consumerRows,
        totalAllocated: consumerRows.reduce((s, c) => s + Number(c.shareHolding), 0),
        status: 'active',
        lastUpdated: new Date().toISOString().slice(0, 10),
      }

      // Editing an approved/rejected mapping invalidates that decision — the
      // allocation the admin signed off on is no longer what's on the record —
      // so a plain save drops it back to draft. Only "Submit for Approval"
      // moves it forward, and only an admin can then approve it.
      const saved = mappingStore.save({ id: mappingId, ...record, approvalStatus: 'draft' })
      if (submit) {
        submitForApproval(saved.id)
      } else {
        message.success(`Consumers mapped to ${selectedPlant?.plantName || 'plant'} saved as draft.`)
      }
      onSaved?.()
    } finally {
      setSaving(false)
    }
  }

  const shareColor = isOver ? '#DC2626' : isBalanced ? '#059669' : '#F59E0B'
  const GRID = 'minmax(150px,1.5fr) 90px 120px 130px 130px 100px 36px'

  return (
    <div>
      {/* Plant select */}
      <div style={{ marginBottom: compact ? 12 : 16 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#4A5568', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
          Plant
          <InfoCircleOutlined style={{ color: '#8A93A6' }} title="A generator can have multiple plants. One active group is allowed per generator + plant." />
        </div>
        <Select
          placeholder={plantOptions.length ? 'Select plant...' : 'This generator has no plants — add plants in Generator Master.'}
          options={plantOptions}
          value={plantId || undefined}
          disabled={!!mappingId}
          showSearch optionFilterProp="label"
          style={{ width: '100%', maxWidth: 520 }}
          onChange={(val) => setPlantId(val)}
        />
        {selectedPlant && (
          <div style={{ marginTop: 12, display: 'flex', gap: 20, flexWrap: 'wrap', background: '#F1F4F9', borderRadius: 10, padding: '10px 16px', border: '1px solid #E4E8F0' }}>
            {[
              { label: 'HT Number', value: selectedPlant.htServiceNumber || '—' },
              { label: 'Location', value: selectedPlant.plantAddress || '—' },
              { label: 'Proposed Annual Generation', value: `${Number(selectedPlant.capacity || 0).toLocaleString('en-IN')} kWh/yr` },
              { label: 'Proposed Monthly Generation', value: `~${formatUnitsKWh(plantMonthlyCapacity)}/mo` },
              { label: 'Substation', value: selectedPlant.substationName || '—' },
            ].map(item => (
              <div key={item.label}>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#8A93A6' }}>{item.label}</div>
                <div style={{ fontWeight: 600, fontSize: 13, color: '#0B1426', marginTop: 2, fontFamily: item.label === 'HT Number' ? 'JetBrains Mono, monospace' : undefined }}>
                  {item.value}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Validation bars */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
        <div style={{ flex: 1, minWidth: 240, background: isOver ? '#FEE2E2' : isBalanced ? '#D1FAE5' : '#FEF3C7', border: `1px solid ${isOver ? '#FECACA' : isBalanced ? '#6EE7B7' : '#FDE68A'}`, borderRadius: 10, padding: '10px 16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#4A5568' }}>Total Allocation %</span>
            <span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, color: shareColor }}>{totalShare}% / 100%</span>
          </div>
          <Progress percent={Math.min(totalShare, 100)} showInfo={false} strokeColor={shareColor} trailColor="#E4E8F0" size="small" style={{ margin: 0 }} />
        </div>
        <div style={{ flex: 1, minWidth: 240, background: unitsOver ? '#FEE2E2' : '#EFF4FF', border: `1px solid ${unitsOver ? '#FECACA' : '#BFDBFE'}`, borderRadius: 10, padding: '10px 16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: '#4A5568' }}>Total Allocated Units / mo</span>
            <span style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, color: unitsOver ? '#DC2626' : '#2563EB' }}>
              {totalUnits.toLocaleString('en-IN')} / {plantMonthlyCapacity ? Math.round(plantMonthlyCapacity).toLocaleString('en-IN') : '—'}
            </span>
          </div>
          <Progress percent={plantMonthlyCapacity ? Math.min((totalUnits / plantMonthlyCapacity) * 100, 100) : 0} showInfo={false}
            strokeColor={unitsOver ? '#DC2626' : '#2563EB'} trailColor="#E4E8F0" size="small" style={{ margin: 0 }} />
        </div>
      </div>

      {/* Consumer rows */}
      <div style={{ overflowX: 'auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: GRID, gap: 10, padding: '8px 12px', background: '#F1F4F9', borderRadius: 8, marginBottom: 10, minWidth: 760 }}>
          {['Consumer', 'Allocation %', 'Allocated Units / mo', 'Effective From', 'Effective To', 'Sakthi ₹', ''].map(h => (
            <div key={h} style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, color: '#4A5568' }}>{h}</div>
          ))}
        </div>

        {consumers.map(row => {
          const consumerCap = consumerMonthlyCapacity(row.consumerId)
          const rowOverConsumer = consumerCap > 0 && Number(row.allocatedUnits ?? 0) > consumerCap
          return (
          <div key={row.key} style={{ display: 'grid', gridTemplateColumns: GRID, gap: 10, padding: '10px 12px', background: '#fff', border: '1px solid #E4E8F0', borderRadius: 8, marginBottom: 8, alignItems: 'center', minWidth: 760 }}>
            <Select placeholder="Consumer..." size="small" style={{ width: '100%' }} showSearch optionFilterProp="label"
              value={row.consumerId || undefined}
              options={consumerOptions.filter(o => !consumers.some(c => c.key !== row.key && c.consumerId === o.value))}
              onChange={val => onConsumerSelect(row.key, val)} />
            <InputNumber placeholder="0" size="small" style={{ width: '100%' }} min={0.01} max={100} step={0.01} precision={2} suffix="%"
              value={row.shareHolding ?? undefined} status={isOver ? 'error' : undefined}
              onChange={val => onShareChange(row.key, val)} />
            <InputNumber placeholder="0" size="small" style={{ width: '100%' }} min={0} step={100}
              value={row.allocatedUnits ?? undefined} status={(unitsOver || rowOverConsumer) ? 'error' : undefined}
              title={consumerCap ? `Consumer's Avg Monthly Consumption = ~${Math.round(consumerCap).toLocaleString('en-IN')} kWh/mo` : undefined}
              formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              onChange={val => onAllocatedUnitsChange(row.key, val)} />
            <DatePicker size="small" style={{ width: '100%' }} format="DD/MM/YYYY" placeholder="From"
              value={row.effectiveFrom} onChange={d => updateConsumer(row.key, 'effectiveFrom', d)} />
            <DatePicker size="small" style={{ width: '100%' }} format="DD/MM/YYYY" placeholder="To (optional)"
              value={row.effectiveTo} onChange={d => updateConsumer(row.key, 'effectiveTo', d)} />
            <InputNumber placeholder="0.00" size="small" style={{ width: '100%' }} min={0} step={0.01} precision={2} prefix="₹"
              value={row.tariff ?? undefined} onChange={val => updateConsumer(row.key, 'tariff', val)} />
            <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => removeConsumer(row.key)} disabled={consumers.length === 1} />
          </div>
          )
        })}
      </div>

      <Button type="dashed" block icon={<PlusOutlined />} onClick={addConsumer} style={{ marginTop: 4, borderRadius: 8 }} disabled={totalShare >= 100}>
        Add Consumer {totalShare >= 100 ? '(100% allocated)' : `(${100 - totalShare}% remaining)`}
      </Button>

      {isOver && (
        <Alert type="error" showIcon style={{ marginTop: 14, borderRadius: 10 }}
          message={`Over-allocated by ${totalShare - 100}%`}
          description="Total allocation % exceeds 100%. Reduce one or more consumers (max 100% per plant group)." />
      )}
      {unitsOver && (
        <Alert type="error" showIcon style={{ marginTop: 12, borderRadius: 10 }}
          message="Allocated units exceed the plant's proposed monthly generation"
          description={`Total allocated units (${totalUnits.toLocaleString('en-IN')}) must not exceed the plant's proposed monthly generation of ~${Math.round(plantMonthlyCapacity).toLocaleString('en-IN')} kWh/mo (derived from its ${plantCapacity.toLocaleString('en-IN')} kWh/yr proposed annual generation).`} />
      )}
      {anyConsumerOver && (
        <Alert type="error" showIcon style={{ marginTop: 12, borderRadius: 10 }}
          message="Allocated units exceed a consumer's Avg Monthly Consumption"
          description={`${overConsumerRows.map(c => masterConsumers.find(m => m.id === c.consumerId)?.name ?? c.consumerId).join(', ')} — a consumer cannot be allocated more than its Avg Monthly Consumption (derived from its Avg Annual Consumption across active HT services).`} />
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 16 }}>
        {onCancel && <Button onClick={onCancel}>Cancel</Button>}
        <Button onClick={() => handleSave(false)} loading={saving} disabled={saving || isOver || unitsOver || anyConsumerOver}>
          Save as Draft
        </Button>
        <Button
          type="primary"
          icon={<SendOutlined />}
          onClick={() => handleSave(true)}
          loading={saving}
          disabled={saving || isOver || unitsOver || anyConsumerOver}
        >
          Submit for Approval
        </Button>
      </div>
    </div>
  )
}

export default ConsumerAllocationEditor
